import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, Chip, Avatar, Grid, TextField,
  Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip,
  LinearProgress, Switch, FormControlLabel, Divider, InputAdornment,
  Drawer, Button, Stack, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Tab, Tabs, Table, TableBody,
  TableCell, TableHead, TableRow, Badge
} from "@mui/material";
import {
  collection, onSnapshot, doc, updateDoc, query,
  where, orderBy, getDocs, limit
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { TIER_COLORS } from "../theme";
import {
  CheckCircle, Cancel, Star, ArrowUpward, ArrowDownward,
  Search, LocationOn, WifiTethering, WifiTetheringOff,
  Block, LockReset, Warning, Person, Work, History,
  AdminPanelSettings, VerifiedUser, GppBad, Refresh
} from "@mui/icons-material";

const TIER_ORDER = ["diamond", "gold", "silver", "bronze"];
const TIER_EMOJIS: Record<string, string> = { diamond: "💎", gold: "🥇", silver: "🥈", bronze: "🥉" };
const RATES: Record<string, number> = { diamond: 0.10, gold: 0.12, silver: 0.15, bronze: 0.18 };
const fns = getFunctions();

function getMarket(tech: any): string {
  // Prefer stored city/state fields (set during onboarding via reverse geocode)
  const city = tech.location?.city;
  const state = tech.location?.state;
  if (city && state) return `${city}, ${state}`;
  if (city) return city;

  // Fall back to bounding-box detection from coordinates
  const lat = tech.location?.lat;
  const lng = tech.location?.lng;
  if (!lat || !lng) return "Unknown";
  if (lat > 35.7 && lat < 36.4 && lng > -80.7 && lng < -79.8) return "Winston-Salem, NC";
  if (lat > 37.0 && lat < 37.6 && lng > -122.5 && lng < -121.5) return "Cupertino, CA";
  if (lat > 27.0 && lat < 27.5 && lng > -80.6 && lng < -80.1) return "Stuart, FL";
  if (lat > 26.3 && lat < 27.0 && lng > -80.3 && lng < -80.0) return "Palm Beach, FL";
  if (lat > 25.5 && lat < 26.3 && lng > -80.5 && lng < -80.0) return "Miami, FL";
  if (lat > 40.4 && lat < 41.0 && lng > -74.3 && lng < -73.7) return "New York, NY";
  if (lat > 33.7 && lat < 34.3 && lng > -118.7 && lng < -118.0) return "Los Angeles, CA";
  if (lat > 41.6 && lat < 42.1 && lng > -87.9 && lng < -87.5) return "Chicago, IL";
  if (lat > 41.0 && lat < 41.4 && lng > -73.4 && lng < -72.8) return "Bridgeport, CT";
  return "Unknown";
}

function fmtDate(ts: any) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Tech Profile Drawer ────────────────────────────────────────────────────
function TechProfileDrawer({ tech, onClose, onRefresh }: { tech: any; onClose: () => void; onRefresh: () => void }) {
  const [tab, setTab] = useState(0);
  const [jobs, setJobs] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [adminLog, setAdminLog] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Confirm dialog
  const [confirm, setConfirm] = useState<{ title: string; body: string; onConfirm: () => void } | null>(null);
  // Ban reason dialog
  const [banDialog, setBanDialog] = useState(false);
  const [banReason, setBanReason] = useState("");
  // Suspend reason dialog
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const isOnline = tech.techStatus === "available" || tech.isOnline;
  const isBusy   = tech.techStatus === "busy";
  const isBanned = tech.isBanned;
  const isSuspended = tech.isSuspended;

  useEffect(() => {
    if (tab === 1) loadJobs();
    if (tab === 2) loadDisputes();
    if (tab === 3) loadAdminLog();
  }, [tab]);

  async function loadJobs() {
    setLoadingJobs(true);
    const snap = await getDocs(query(
      collection(db, "jobs"),
      where("technicianId", "==", tech.id),
      orderBy("createdAt", "desc"),
      limit(20)
    ));
    setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoadingJobs(false);
  }

  async function loadDisputes() {
    setLoadingJobs(true);
    const snap = await getDocs(query(
      collection(db, "disputes"),
      where("techId", "==", tech.id),
      orderBy("createdAt", "desc"),
      limit(20)
    ));
    setDisputes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoadingJobs(false);
  }

  async function loadAdminLog() {
    setLoadingJobs(true);
    const snap = await getDocs(query(
      collection(db, "adminActions"),
      where("targetUid", "==", tech.id),
      orderBy("createdAt", "desc"),
      limit(20)
    ));
    setAdminLog(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoadingJobs(false);
  }

  async function call(fnName: string, payload: any, successMsg: string) {
    setBusy(true);
    setMsg(null);
    try {
      await httpsCallable(fns, fnName)(payload);
      setMsg({ text: successMsg, type: "success" });
      onRefresh();
    } catch (e: any) {
      setMsg({ text: e.message || "Error", type: "error" });
    }
    setBusy(false);
  }

  const handleToggleOnline = () => call(
    "banUser", // we just use firestore directly for this one
    {},
    ""
  );

  const handleForceOnline = async (online: boolean) => {
    setBusy(true);
    await updateDoc(doc(db, "technicians", tech.id), {
      techStatus: online ? "available" : "offline",
      isOnline: online,
    });
    setMsg({ text: online ? "Tech set to available" : "Tech set to offline", type: "success" });
    setBusy(false);
    onRefresh();
  };

  const handleBan = async () => {
    await call("banUser", { uid: tech.id, reason: banReason, collection: "technicians" }, "Tech account banned");
    setBanDialog(false);
    setBanReason("");
  };

  const handleUnban = () => setConfirm({
    title: "Unban Tech",
    body: `Re-enable ${tech.displayName}'s account?`,
    onConfirm: () => call("unbanUser", { uid: tech.id, collection: "technicians" }, "Tech account unbanned"),
  });

  const handleSuspend = async () => {
    await call("suspendTech", { techId: tech.id, reason: suspendReason }, "Tech suspended");
    setSuspendDialog(false);
    setSuspendReason("");
  };

  const handleUnsuspend = () => setConfirm({
    title: "Unsuspend Tech",
    body: `Restore ${tech.displayName} to active status?`,
    onConfirm: () => call("unsuspendTech", { techId: tech.id }, "Tech unsuspended"),
  });

  const handlePasswordReset = () => setConfirm({
    title: "Send Password Reset",
    body: `Send a password reset link to ${tech.email}?`,
    onConfirm: async () => {
      setBusy(true);
      setMsg(null);
      try {
        const result: any = await httpsCallable(fns, "sendPasswordReset")({ email: tech.email });
        setMsg({ text: `Reset link generated. Share with tech: ${result.data.link}`, type: "success" });
      } catch (e: any) {
        setMsg({ text: e.message, type: "error" });
      }
      setBusy(false);
    },
  });

  const handlePromote = async () => {
    const idx = TIER_ORDER.indexOf(tech.tier);
    if (idx <= 0) return;
    const newTier = TIER_ORDER[idx - 1];
    await call("adminUpdateTech", { techId: tech.id, updates: { tier: newTier } }, `Promoted to ${newTier}`);
  };

  const handleDemote = async () => {
    const idx = TIER_ORDER.indexOf(tech.tier);
    if (idx >= TIER_ORDER.length - 1) return;
    const newTier = TIER_ORDER[idx + 1];
    setConfirm({
      title: "Demote Tech",
      body: `Demote ${tech.displayName} from ${tech.tier} → ${newTier}?`,
      onConfirm: () => call("adminUpdateTech", { techId: tech.id, updates: { tier: newTier } }, `Demoted to ${newTier}`),
    });
  };

  const handleVerify = () => setConfirm({
    title: "Mark as Verified",
    body: `Manually verify ${tech.displayName}? This bypasses Checkr.`,
    onConfirm: () => call("adminUpdateTech", { techId: tech.id, updates: { isVerified: true, checkrStatus: "clear" } }, "Tech manually verified"),
  });

  const statusColor = isBanned ? "error" : isSuspended ? "warning" : isBusy ? "info" : isOnline ? "success" : "default";
  const statusLabel = isBanned ? "Banned" : isSuspended ? "Suspended" : isBusy ? "On a Job" : isOnline ? "Available" : "Offline";

  return (
    <>
      <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, bgcolor: "background.default", p: 3 } }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: isBanned ? "error.main" : isOnline ? "success.main" : "grey.500", border: "2px solid", borderColor: "background.default" }} />
            }
          >
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: 28 }}>
              {tech.displayName?.[0]}
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>{tech.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">{tech.email}</Typography>
            <Typography variant="caption" color="text.secondary">{tech.phone}</Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              <Chip label={`${TIER_EMOJIS[tech.tier]} ${tech.tier}`} size="small"
                sx={{ bgcolor: TIER_COLORS[tech.tier] + "22", color: TIER_COLORS[tech.tier], fontWeight: 700, textTransform: "capitalize" }} />
              <Chip label={statusLabel} size="small" color={statusColor as any} variant="outlined" />
              <Chip label={getMarket(tech)} size="small" icon={<LocationOn sx={{ fontSize: 12 }} />} variant="outlined" />
            </Box>
          </Box>
          <IconButton onClick={onClose}><Cancel /></IconButton>
        </Box>

        {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

        {/* Quick stats */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 1.5, mb: 3 }}>
          {[
            { label: "Jobs", value: tech.tierScore?.totalJobs ?? tech.totalJobs ?? 0 },
            { label: "Rating", value: `${(tech.tierScore?.avgRating ?? tech.rating ?? 0).toFixed(1)}★` },
            { label: "Success", value: `${(tech.tierScore?.successRate ?? 0).toFixed(0)}%` },
            { label: "Rate", value: `$${tech.hourlyRate}/hr` },
          ].map(s => (
            <Box key={s.label} sx={{ textAlign: "center", bgcolor: "rgba(255,255,255,0.05)", borderRadius: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
          <Tab icon={<Person />} iconPosition="start" label="Profile" />
          <Tab icon={<Work />} iconPosition="start" label="Jobs" />
          <Tab icon={<Warning />} iconPosition="start" label="Disputes" />
          <Tab icon={<History />} iconPosition="start" label="Admin Log" />
        </Tabs>

        {/* ── Profile tab ── */}
        {tab === 0 && (
          <Box>
            {/* Background check */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Background Check</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {tech.checkrStatus === "clear"
                    ? <CheckCircle color="success" />
                    : tech.checkrStatus === "consider"
                    ? <Warning color="warning" />
                    : <Cancel color="error" />}
                  <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                    {tech.checkrStatus || "Not started"}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Verified: {tech.isVerified ? "Yes ✅" : "No ❌"} · 
                  Stripe onboarding: {tech.stripeOnboardingComplete ? "Complete ✅" : "Incomplete ❌"}
                </Typography>
                {!tech.isVerified && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" startIcon={<VerifiedUser />} onClick={handleVerify}>
                      Manually Verify
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Rating breakdown */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Performance</Typography>
                {[
                  { label: "Avg Rating", value: (tech.tierScore?.avgRating ?? 0).toFixed(1), max: 5, pct: ((tech.tierScore?.avgRating ?? 0) / 5) * 100, color: "#FFD700" },
                  { label: "Success Rate", value: `${(tech.tierScore?.successRate ?? 0).toFixed(0)}%`, max: 100, pct: tech.tierScore?.successRate ?? 0, color: "#22C55E" },
                  { label: "Avg Response", value: `${(tech.tierScore?.avgResponseTimeMinutes ?? 0).toFixed(0)} min`, max: 30, pct: Math.max(0, 100 - ((tech.tierScore?.avgResponseTimeMinutes ?? 30) / 30) * 100), color: "#3B82F6" },
                ].map(s => (
                  <Box key={s.label} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      <Typography variant="caption" fontWeight={700}>{s.value}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={s.pct}
                      sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: s.color } }} />
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  {tech.tierScore?.successfulJobs ?? 0} completed · {tech.tierScore?.couldntFixJobs ?? 0} couldn't fix · 
                  ${(tech.tierScore?.totalEarnings ?? 0).toFixed(0)} total earned
                </Typography>
              </CardContent>
            </Card>

            {/* Categories */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Categories & Specialties</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                  {(tech.categories || []).map((c: string) => (
                    <Chip key={c} label={c.replace(/_/g, " ")} size="small" variant="outlined" />
                  ))}
                </Box>
                {(tech.specialties || []).length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, mb: 0.5 }}>Specialties</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {tech.specialties.map((s: string) => (
                        <Chip key={s} label={s} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tier management */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Tier Management</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <Chip label={`${TIER_EMOJIS[tech.tier]} ${tech.tier}`} size="small"
                    sx={{ bgcolor: TIER_COLORS[tech.tier] + "22", color: TIER_COLORS[tech.tier], fontWeight: 700, textTransform: "capitalize" }} />
                  <Typography variant="caption" color="text.secondary">
                    Commission: {(RATES[tech.tier] * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" startIcon={<ArrowUpward />}
                    disabled={tech.tier === "diamond" || busy} onClick={handlePromote}>
                    Promote
                  </Button>
                  <Button size="small" variant="outlined" color="warning" startIcon={<ArrowDownward />}
                    disabled={tech.tier === "bronze" || busy} onClick={handleDemote}>
                    Demote
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Account actions */}
            <Card>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Account Actions</Typography>
                <Stack spacing={1.5}>
                  {/* Online toggle */}
                  <Button fullWidth variant="outlined" size="small"
                    startIcon={isOnline ? <WifiTetheringOff /> : <WifiTethering />}
                    color={isOnline ? "warning" : "success"}
                    disabled={busy || isBanned}
                    onClick={() => handleForceOnline(!isOnline)}>
                    {isOnline ? "Force Offline" : "Set Available"}
                  </Button>

                  {/* Suspend / Unsuspend */}
                  {isSuspended ? (
                    <Button fullWidth variant="outlined" size="small" color="success"
                      startIcon={<CheckCircle />} disabled={busy} onClick={handleUnsuspend}>
                      Lift Suspension
                    </Button>
                  ) : (
                    <Button fullWidth variant="outlined" size="small" color="warning"
                      startIcon={<Warning />} disabled={busy || isBanned} onClick={() => setSuspendDialog(true)}>
                      Suspend Tech
                    </Button>
                  )}

                  {/* Ban / Unban */}
                  {isBanned ? (
                    <Button fullWidth variant="outlined" size="small" color="success"
                      startIcon={<CheckCircle />} disabled={busy} onClick={handleUnban}>
                      Unban Account
                    </Button>
                  ) : (
                    <Button fullWidth variant="outlined" size="small" color="error"
                      startIcon={<Block />} disabled={busy} onClick={() => setBanDialog(true)}>
                      Ban Account
                    </Button>
                  )}

                  {/* Password reset */}
                  <Button fullWidth variant="outlined" size="small" color="info"
                    startIcon={<LockReset />} disabled={busy} onClick={handlePasswordReset}>
                    Send Password Reset
                  </Button>
                </Stack>
                {isBanned && (
                  <Alert severity="error" sx={{ mt: 2 }} icon={<Block />}>
                    Banned: {tech.banReason || "No reason given"} · {fmtDate(tech.bannedAt)}
                  </Alert>
                )}
                {isSuspended && !isBanned && (
                  <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                    Suspended: {tech.suspendReason || "No reason given"} · {fmtDate(tech.suspendedAt)}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {/* ── Jobs tab ── */}
        {tab === 1 && (
          <Box>
            {loadingJobs ? <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} /> : (
              jobs.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" mt={4}>No jobs found</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Earned</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map(j => (
                      <TableRow key={j.id}>
                        <TableCell><Typography variant="caption">{fmtDate(j.createdAt)}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{(j.category || "").replace(/_/g, " ")}</Typography></TableCell>
                        <TableCell>
                          <Chip label={j.status} size="small"
                            color={j.status === "completed" ? "success" : j.status === "cancelled" ? "error" : "default"} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">${(j.pricing?.techEarnings || 0).toFixed(2)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            )}
          </Box>
        )}

        {/* ── Disputes tab ── */}
        {tab === 2 && (
          <Box>
            {loadingJobs ? <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} /> : (
              disputes.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" mt={4}>No disputes on record ✅</Typography>
              ) : (
                disputes.map(d => (
                  <Card key={d.id} sx={{ mb: 1.5 }}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" fontWeight={600}>{d.reason || "Dispute"}</Typography>
                        <Chip label={d.status} size="small"
                          color={d.status === "resolved_refunded" ? "success" : d.status === "open" ? "error" : "default"} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{fmtDate(d.createdAt)}</Typography>
                    </CardContent>
                  </Card>
                ))
              )
            )}
          </Box>
        )}

        {/* ── Admin Log tab ── */}
        {tab === 3 && (
          <Box>
            <Button size="small" startIcon={<Refresh />} onClick={loadAdminLog} sx={{ mb: 2 }}>Refresh</Button>
            {loadingJobs ? <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} /> : (
              adminLog.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" mt={4}>No admin actions yet</Typography>
              ) : (
                adminLog.map(a => (
                  <Box key={a.id} sx={{ mb: 1.5, pl: 1.5, borderLeft: "3px solid", borderColor: "primary.main" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: "capitalize" }}>
                      {a.action.replace(/_/g, " ")}
                    </Typography>
                    {a.reason && <Typography variant="caption" color="text.secondary">Reason: {a.reason}</Typography>}
                    <Typography variant="caption" color="text.secondary" display="block">{fmtDate(a.createdAt)}</Typography>
                  </Box>
                ))
              )
            )}
          </Box>
        )}
      </Drawer>

      {/* Confirm dialog */}
      {confirm && (
        <Dialog open onClose={() => setConfirm(null)}>
          <DialogTitle>{confirm.title}</DialogTitle>
          <DialogContent><Typography>{confirm.body}</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => { confirm.onConfirm(); setConfirm(null); }}>Confirm</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Ban reason dialog */}
      <Dialog open={banDialog} onClose={() => setBanDialog(false)}>
        <DialogTitle>Ban Tech Account</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>This disables {tech.displayName}'s login immediately.</Typography>
          <TextField fullWidth size="small" label="Reason (required)" value={banReason}
            onChange={e => setBanReason(e.target.value)} placeholder="e.g. Policy violation, fraud..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={!banReason.trim() || busy} onClick={handleBan}>Ban Account</Button>
        </DialogActions>
      </Dialog>

      {/* Suspend reason dialog */}
      <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
        <DialogTitle>Suspend Tech</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Tech will be taken offline and blocked from accepting new jobs. Account stays active.</Typography>
          <TextField fullWidth size="small" label="Reason" value={suspendReason}
            onChange={e => setSuspendReason(e.target.value)} placeholder="e.g. Under investigation, complaint received..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={handleSuspend}>Suspend</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─── Main TechsView ─────────────────────────────────────────────────────────
export function TechsView() {
  const [techs, setTechs] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMarket, setFilterMarket] = useState("all");
  const [groupByMarket, setGroupByMarket] = useState(false);
  const [selectedTech, setSelectedTech] = useState<any | null>(null);

  useEffect(() => {
    return onSnapshot(collection(db, "technicians"), (snap) => {
      setTechs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const techsWithMarket = techs.map(t => ({ ...t, _market: getMarket(t) }));
  const markets = ["all", ...Array.from(new Set(techsWithMarket.map(t => t._market))).sort()];

  const filtered = techsWithMarket
    .filter(t => !search ||
      t.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      t.email?.toLowerCase().includes(search.toLowerCase()) ||
      t._market?.toLowerCase().includes(search.toLowerCase()))
    .filter(t => filterTier === "all" || t.tier === filterTier)
    .filter(t => filterStatus === "all" ||
      (filterStatus === "online"   ? (t.techStatus === "available" || t.isOnline) :
       filterStatus === "busy"     ? t.techStatus === "busy" :
       filterStatus === "suspended"? t.isSuspended :
       filterStatus === "banned"   ? t.isBanned :
       !(t.techStatus === "available" || t.techStatus === "busy" || t.isOnline)))
    .filter(t => filterMarket === "all" || t._market === filterMarket)
    .sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));

  const onlineCount    = techsWithMarket.filter(t => t.techStatus === "available" || t.isOnline).length;
  const busyCount      = techsWithMarket.filter(t => t.techStatus === "busy").length;
  const suspendedCount = techsWithMarket.filter(t => t.isSuspended).length;

  const handlePromote = async (tech: any) => {
    const idx = TIER_ORDER.indexOf(tech.tier);
    if (idx <= 0) return;
    const newTier = TIER_ORDER[idx - 1];
    await updateDoc(doc(db, "technicians", tech.id), { tier: newTier, commissionRate: RATES[newTier] });
  };

  const handleDemote = async (tech: any) => {
    const idx = TIER_ORDER.indexOf(tech.tier);
    if (idx >= TIER_ORDER.length - 1) return;
    const newTier = TIER_ORDER[idx + 1];
    await updateDoc(doc(db, "technicians", tech.id), { tier: newTier, commissionRate: RATES[newTier] });
  };

  const handleToggleOnline = async (tech: any) => {
    const isCurrentlyOnline = tech.techStatus === "available" || tech.isOnline;
    await updateDoc(doc(db, "technicians", tech.id), {
      techStatus: isCurrentlyOnline ? "offline" : "available",
      isOnline: !isCurrentlyOnline,
    });
  };

  const marketGroups: Record<string, any[]> = {};
  if (groupByMarket) {
    filtered.forEach(t => {
      if (!marketGroups[t._market]) marketGroups[t._market] = [];
      marketGroups[t._market].push(t);
    });
  }

  const renderTechCard = (tech: any) => {
    const isOnline = tech.techStatus === "available" || tech.isOnline;
    const isBusy   = tech.techStatus === "busy";
    const isBanned = tech.isBanned;
    const isSuspended = tech.isSuspended;
    const statusLabel = isBanned ? "Banned" : isSuspended ? "Suspended" : isBusy ? "On a Job" : isOnline ? "Available" : "Offline";
    const statusColor: any = isBanned ? "error" : isSuspended ? "warning" : isBusy ? "info" : isOnline ? "success" : "default";

    return (
      <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={tech.id}>
        <Card sx={{ cursor: "pointer", border: selectedTech?.id === tech.id ? "1px solid" : "1px solid transparent", borderColor: selectedTech?.id === tech.id ? "primary.main" : "transparent" }}
          onClick={() => setSelectedTech(tech)}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: isBanned ? "error.main" : "primary.main", width: 48, height: 48 }}>
                {tech.displayName?.[0] || "T"}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>{tech.displayName}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <LocationOn sx={{ fontSize: 12, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary" noWrap>{tech._market}</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`${TIER_EMOJIS[tech.tier]} ${tech.tier}`} size="small"
                    sx={{ bgcolor: TIER_COLORS[tech.tier] + "22", color: TIER_COLORS[tech.tier], fontWeight: 700, textTransform: "capitalize" }} />
                  <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 2 }}>
              {[
                { label: "Jobs", value: tech.tierScore?.totalJobs ?? tech.totalJobs ?? 0 },
                { label: "Rating", value: `${(tech.tierScore?.avgRating ?? tech.rating ?? 0).toFixed(1)}★` },
                { label: "Rate", value: `$${tech.hourlyRate}/hr` },
              ].map(s => (
                <Box key={s.label} sx={{ textAlign: "center", bgcolor: "rgba(255,255,255,0.04)", borderRadius: 1.5, py: 1 }}>
                  <Typography variant="body2" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <LinearProgress variant="determinate"
                value={(tech.tierScore?.avgRating ?? tech.rating ?? 0) / 5 * 100}
                sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: "#FFD700" } }} />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {tech.checkrStatus === "clear"
                  ? <CheckCircle sx={{ fontSize: 14, color: "success.main" }} />
                  : <Cancel sx={{ fontSize: 14, color: "warning.main" }} />}
                <Typography variant="caption" color={tech.checkrStatus === "clear" ? "success.main" : "warning.main"}>
                  {tech.checkrStatus || "pending"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }} onClick={e => e.stopPropagation()}>
                <Tooltip title="Promote tier">
                  <span><IconButton size="small" onClick={() => handlePromote(tech)} disabled={tech.tier === "diamond"}>
                    <ArrowUpward fontSize="small" />
                  </IconButton></span>
                </Tooltip>
                <Tooltip title="Demote tier">
                  <span><IconButton size="small" onClick={() => handleDemote(tech)} disabled={tech.tier === "bronze"}>
                    <ArrowDownward fontSize="small" />
                  </IconButton></span>
                </Tooltip>
                <Tooltip title={isOnline ? "Force offline" : "Set available"}>
                  <IconButton size="small" onClick={() => handleToggleOnline(tech)}>
                    {isOnline ? <WifiTetheringOff fontSize="small" color="error" /> : <WifiTethering fontSize="small" color="success" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      {/* Summary bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total Techs", value: techs.length, color: "text.primary" },
          { label: "Available", value: onlineCount, color: "success.main" },
          { label: "On a Job", value: busyCount, color: "info.main" },
          { label: "Suspended", value: suspendedCount, color: "warning.main" },
          { label: "Markets", value: markets.length - 1, color: "primary.main" },
        ].map(s => (
          <Box key={s.label} sx={{ bgcolor: "rgba(255,255,255,0.04)", borderRadius: 2, px: 2.5, py: 1.5, minWidth: 100 }}>
            <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField size="small" placeholder="Search name, email, city…" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Market</InputLabel>
          <Select value={filterMarket} label="Market" onChange={e => setFilterMarket(e.target.value)}>
            {markets.map(m => <MenuItem key={m} value={m}>{m === "all" ? "All Markets" : m}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Tier</InputLabel>
          <Select value={filterTier} label="Tier" onChange={e => setFilterTier(e.target.value)}>
            <MenuItem value="all">All Tiers</MenuItem>
            {TIER_ORDER.map(t => <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>{TIER_EMOJIS[t]} {t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="online">Available</MenuItem>
            <MenuItem value="busy">On a Job</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
            <MenuItem value="banned">Banned</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={groupByMarket} onChange={e => setGroupByMarket(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Group by market</Typography>}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {filtered.length} of {techs.length} techs · Click any card to view full profile
        </Typography>
      </Box>

      {groupByMarket ? (
        Object.entries(marketGroups).map(([market, mTechs]) => (
          <Box key={market} sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <LocationOn color="primary" />
              <Typography variant="h6" fontWeight={700}>{market}</Typography>
              <Chip label={`${mTechs.length} tech${mTechs.length !== 1 ? "s" : ""}`} size="small" />
              <Chip label={`${mTechs.filter(t => t.techStatus === "available" || t.isOnline).length} available`}
                size="small" color="success" variant="outlined" />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>{mTechs.map(renderTechCard)}</Grid>
          </Box>
        ))
      ) : (
        <Grid container spacing={2}>{filtered.map(renderTechCard)}</Grid>
      )}

      {/* Profile drawer */}
      {selectedTech && (
        <TechProfileDrawer
          tech={techsWithMarket.find(t => t.id === selectedTech.id) ?? selectedTech}
          onClose={() => setSelectedTech(null)}
          onRefresh={() => {/* live listener handles it */}}
        />
      )}
    </Box>
  );
}
